import { FC, useContext, useMemo, useState } from 'react';

import {
  CheckIcon,
  H2,
  Input,
  Modal,
  OpenedEyeIcon,
  Preloader,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';

import { ITemplatesPlaysModalProps } from './interfaces';
import s from './TemplatesPlaysModal.module.scss';
import { groupByAlphabet } from './utils';

export const TemplatesPlaysModal: FC<ITemplatesPlaysModalProps> = ({
  shortcutTemplates,
  onClose,
  setOpenTemplate,
  onApplyTemplate,
  getTemplateById,
  getTemplateLoading,
}) => {
  const { t } = useContext(LocalizationContext);
  const [search, setSearch] = useState('');

  const safeTemplates = shortcutTemplates ?? [];

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return safeTemplates;

    const lower = search.toLowerCase();
    return safeTemplates.filter((tpl) =>
      tpl.title.toLowerCase().includes(lower)
    );
  }, [search, safeTemplates]);

  const groupedTemplates = useMemo(
    () => groupByAlphabet(filteredTemplates),
    [filteredTemplates]
  );

  const hasResults = Object.keys(groupedTemplates).length > 0;

  return (
    <Modal className={s.container} onClose={onClose}>
      <H2>{t('templates.modal.title')}</H2>

      <Input
        value={search}
        onChange={setSearch}
        placeholder={t('application.game.title.placeholder')}
        iconName="SearchIcon"
        variant="secondary"
        disabled={getTemplateLoading}
      />

      <div
        className={cn(s.list, !hasResults && s.list_empty)}
        data-onboarding="shortcut-template-library"
      >
        <Preloader loading={getTemplateLoading}>
          {!hasResults && (
            <div className={s.empty}>{t('templates.modal.empty')}</div>
          )}

          {Object.entries(groupedTemplates).map(([letter, templates]) => (
            <div key={letter} className={s.group}>
              <div className={s.letter_block}>
                <span className={s.letter}>{letter}</span>
              </div>
              {templates.map((tpl) => (
                <div key={tpl.id} className={s.item}>
                  <div className={s.left}>
                    <span className={s.title}>{tpl.title}</span>

                    {tpl.age_rating !== null && (
                      <span className={s.age}>{tpl.age_rating}+</span>
                    )}
                  </div>

                  <div className={s.right}>
                    <Tooltip
                      contentClassName={s.tooltip}
                      position="top"
                      content={t('templates.modal.open.tooltip')}
                    >
                      <div
                        onClick={() => {
                          setOpenTemplate(tpl?.id);
                          onClose();
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            setOpenTemplate(tpl.id);
                            onClose();
                          }
                        }}
                        className={s.button}
                        role="button"
                        tabIndex={0}
                        aria-label={`${t('templates.modal.open.tooltip')}: ${
                          tpl.title
                        }`}
                      >
                        <OpenedEyeIcon color="link-info" />
                      </div>
                    </Tooltip>
                    <Tooltip
                      contentClassName={s.tooltip}
                      position="top"
                      content={t('templates.modal.change.tooltip')}
                    >
                      <div
                        className={s.button}
                        role="button"
                        tabIndex={0}
                        aria-label={`${t('templates.modal.change.tooltip')}: ${
                          tpl.title
                        }`}
                        onClick={() => {
                          getTemplateById({
                            variables: {
                              shortcutTemplateId: tpl.id,
                            },
                          }).then((res) => {
                            if (res?.data?.shortcutTemplate) {
                              onApplyTemplate(res.data.shortcutTemplate);
                              onClose();
                            }
                          });
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ')
                            return;
                          getTemplateById({
                            variables: { shortcutTemplateId: tpl.id },
                          }).then((res) => {
                            if (res?.data?.shortcutTemplate) {
                              onApplyTemplate(res.data.shortcutTemplate);
                              onClose();
                            }
                          });
                        }}
                      >
                        <CheckIcon color="link-info" />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </Preloader>
      </div>
    </Modal>
  );
};
